import { AbstractControl, FormControl, FormGroup, ValidationErrors } from '@angular/forms';

// ===== FUNCTIONS PURAS (para tree-shaking máximo) =====

/**
 * Remove espaços em branco no início e fim de todos os campos string do FormGroup
 */
export function removerEspacos(formGroup: FormGroup): void {
  Object.keys(formGroup.controls).forEach((key) => {
    const control = formGroup.get(key);
    if (control instanceof FormControl && typeof control.value === 'string') {
      const trimmed = control.value.trim();
      control.setValue(trimmed, { emitEvent: false });
    }
  });
}

/**
 * Remove máscara de um campo específico do FormGroup
 */
export function removerMascara(formGroup: FormGroup, campo: string): void {
  const control = formGroup.get(campo);
  if (control && typeof control.value === 'string') {
    const valorSemMascara = control.value.replace(/\D/g, '');
    control.setValue(valorSemMascara, { emitEvent: false });
  }
}

/**
 * Remove máscaras de múltiplos campos
 */
export function removerMascaras(formGroup: FormGroup, campos: string[]): void {
  campos.forEach((campo) => removerMascara(formGroup, campo));
}

/**
 * Limpa todo o FormGroup
 */
export function limparFormulario(formGroup: FormGroup): void {
  formGroup.reset();
  Object.keys(formGroup.controls).forEach((key) => {
    formGroup.get(key)?.setErrors(null);
  });
}

/**
 * Marca todos os campos como touched para exibir validações
 */
export function marcarCamposTouched(formGroup: FormGroup): void {
  Object.keys(formGroup.controls).forEach((key) => {
    const control = formGroup.get(key);
    control?.markAsTouched();

    // Se for um FormGroup aninhado, marcar recursivamente
    if (control instanceof FormGroup) {
      marcarCamposTouched(control);
    }
  });
}

/**
 * Verifica se algum campo do FormGroup está inválido e touched
 */
export function temErrosVisiveis(formGroup: FormGroup): boolean {
  return Object.keys(formGroup.controls).some((key) => {
    const control = formGroup.get(key);
    return control?.invalid && control?.touched;
  });
}

/**
 * Obtém o primeiro erro de um campo específico
 */
export function obterPrimeiroErro(
  formGroup: FormGroup,
  campo: string
): string | null {
  const control = formGroup.get(campo);
  if (control?.errors && control.touched) {
    const errors = control.errors;

    if (errors['required']) return 'Campo obrigatório';
    if (errors['email']) return 'E-mail inválido';
    if (errors['minlength'])
      return `Mínimo ${errors['minlength'].requiredLength} caracteres`;
    if (errors['maxlength'])
      return `Máximo ${errors['maxlength'].requiredLength} caracteres`;
    if (errors['pattern']) return 'Formato inválido';
    if (errors['cpfInvalido']) return 'CPF inválido';
    if (errors['cnpjInvalido']) return 'CNPJ inválido';
    if (errors['telefoneInvalido']) return 'Telefone inválido';
    if (errors['cepInvalido']) return 'CEP inválido';

    return 'Campo inválido';
  }

  return null;
}

/**
 * Preenche FormGroup com dados de um objeto
 */
export function preencherFormulario<T>(
  formGroup: FormGroup,
  dados: Partial<T>
): void {
  Object.keys(dados).forEach((key) => {
    if (formGroup.get(key) && dados[key as keyof T] !== undefined) {
      formGroup.get(key)?.setValue(dados[key as keyof T], { emitEvent: false });
    }
  });
}

/**
 * Verifica se o FormGroup está vazio (todos os campos sem valor)
 */
export function formularioVazio(formGroup: FormGroup): boolean {
  return Object.keys(formGroup.controls).every((key) => {
    const control = formGroup.get(key);
    const valor = control?.value;
    return valor === null || valor === undefined || valor === '';
  });
}

/**
 * Obtém todos os valores do FormGroup apenas dos campos válidos
 */
export function obterValoresValidos<T>(formGroup: FormGroup): Partial<T> {
  const valores: any = {};

  Object.keys(formGroup.controls).forEach((key) => {
    const control = formGroup.get(key);
    if (
      control?.valid &&
      control.value !== null &&
      control.value !== undefined &&
      control.value !== ''
    ) {
      valores[key] = control.value;
    }
  });

  return valores;
}

/**
 * Validator de grupo: rejeita quando horaFechamento <= horaAbertura.
 * Aceita Date (p-datepicker desktop) ou string "HH:mm" (input nativo mobile).
 */
export function fechamentoMaiorQueAbertura(group: AbstractControl): ValidationErrors | null {
  const abertura = group.get('horaAbertura')?.value;
  const fechamento = group.get('horaFechamento')?.value;
  if (!abertura || !fechamento) return null;

  const toMinutes = (v: Date | string): number => {
    if (typeof v === 'string') {
      const [h, m] = v.split(':');
      return parseInt(h) * 60 + parseInt(m);
    }
    return v.getHours() * 60 + v.getMinutes();
  };

  return toMinutes(fechamento) > toMinutes(abertura)
    ? null
    : { fechamentoMenorQueAbertura: true };
}

// ===== CLASSE ESTÁTICA (para organização e compatibilidade) =====

/**
 * Classe estática com todos os utilitários de formulário
 * Use quando preferir organização por namespace: FormUtils.removerEspacos()
 */
export class FormUtils {
  static removerEspacos = removerEspacos;
  static removerMascara = removerMascara;
  static removerMascaras = removerMascaras;
  static limparFormulario = limparFormulario;
  static marcarCamposTouched = marcarCamposTouched;
  static temErrosVisiveis = temErrosVisiveis;
  static obterPrimeiroErro = obterPrimeiroErro;
  static preencherFormulario = preencherFormulario;
  static formularioVazio = formularioVazio;
  static obterValoresValidos = obterValoresValidos;
  static fechamentoMaiorQueAbertura = fechamentoMaiorQueAbertura;
}
